import React, { useState } from 'react';
import { CaretLeft, Check, CircleNotch, House, Sparkle, SquaresFour, Sun } from '@phosphor-icons/react';
import { isEnFinLossesVendor, type ActiveLossesVendor } from '../../Constants';
import { useCurrentUser } from '../../contexts/UserContext';
import type { DesignData } from '../../@types/types';
import {
    annualProductionKwhForPricingFromRaw,
    annualProductionKwhFromSnapshot,
    parseProductionRawScalar,
} from '../../helpers/systemDesignSolarMetrics';
import { countIncludedModules, estimateSelectedAnnualKwh } from '../lib/preContractModuleLayout';
import {
    averageIncludedMultiplier,
    roofIncludedMultiplierDetail,
    type PreContractPpwMetricsOverride,
} from '../lib/preContractPpwPricing';
import {
    lossesSourceLabel,
    type PreContractLossesPricingCard,
} from '../lib/usePreContractLossesPricing';
import type { PreContractIntakeForm } from '../lib/preContractTypes';
import { RoofModuleBoard } from './PreContractModuleStep';

function designKey(design: DesignData): string {
    return `${design.Design_No}-${design.Version_No}`;
}

function getLocalTime(isoString: string | undefined): string {
    if (!isoString) return '';
    const globalTime = new Date(isoString);
    if (Number.isNaN(globalTime.getTime())) return '';
    const localTime = new Date(globalTime.getTime() - globalTime.getTimezoneOffset() * 60000);
    return localTime.toLocaleString();
}

function formatKw(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value) || value <= 0) return '—';
    return `${value.toFixed(2)} kW`;
}

function formatKwh(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value) || value <= 0) return '—';
    return `${Math.round(value).toLocaleString()} kWh`;
}

type AllDesignsLayoutFocus =
    | { kind: 'saved'; key: string }
    | { kind: 'max'; vendor: ActiveLossesVendor };

type AllDesignsCardShellProps = {
    className: string;
    selected: boolean;
    selecting: boolean;
    onSelect: () => void;
    onViewLayout: () => void;
    children: React.ReactNode;
};

const AllDesignsCardShell: React.FC<AllDesignsCardShellProps> = ({
    className,
    selected,
    selecting,
    onSelect,
    onViewLayout,
    children,
}) => {
    const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        onSelect();
    };

    return (
        <article
            className={className}
            aria-pressed={selected}
            aria-busy={selecting || undefined}
            tabIndex={0}
            onClick={onSelect}
            onKeyDown={handleKeyDown}
        >
            {children}
            <button
                type="button"
                className="pci-ai-config-sub__toggle pci-all-designs__layout-open"
                aria-label="Edit roof layout"
                title="Edit roof layout"
                onClick={(event) => {
                    event.stopPropagation();
                    onViewLayout();
                }}
                onKeyDown={(event) => event.stopPropagation()}
            >
                <SquaresFour size={14} weight="bold" aria-hidden />
            </button>
        </article>
    );
};

export type PreContractSavedDesignsPanelProps = {
    designs: DesignData[];
    loading?: boolean;
    selectedKey?: string | null;
    selectingKey?: string | null;
    onSelect: (design: DesignData) => void;
    maxLayoutCards?: PreContractLossesPricingCard[];
    maxLayoutSystemKw?: number;
    maxLayoutModuleCount?: number;
    selectedMaxVendor?: ActiveLossesVendor | null;
    maxLayoutLoading?: boolean;
    onSelectMaxLayout?: (vendor: ActiveLossesVendor) => void;
    onToggleModule?: (roofIndex: number, moduleIndex: number) => void;
    onToggleRoof?: (roofIndex: number, included: boolean) => void;
    /** Live roof grids used for the in-card layout preview. */
    form?: Pick<
        PreContractIntakeForm,
        | 'includeGreyPanels'
        | 'includePurplePanels'
        | 'roofModuleLayouts'
        | 'roofSelections'
        | 'moduleTypeWattage'
    > | null;
};

export const PreContractSavedDesignsPanel: React.FC<PreContractSavedDesignsPanelProps> = ({
    designs,
    loading = false,
    selectedKey = null,
    selectingKey = null,
    onSelect,
    maxLayoutCards = [],
    maxLayoutSystemKw = 0,
    maxLayoutModuleCount = 0,
    selectedMaxVendor = null,
    maxLayoutLoading = false,
    onSelectMaxLayout,
    onToggleModule,
    onToggleRoof,
    form = null,
}) => {
    const { hasCapability } = useCurrentUser();
    const showLosses = hasCapability('Losses_Vendors');
    const layouts = form?.roofModuleLayouts ?? [];
    const roofSelections = form?.roofSelections ?? [];
    const includeGreyPanels = form?.includeGreyPanels ?? true;
    const includePurplePanels = form?.includePurplePanels ?? true;
    const [layoutFocus, setLayoutFocus] = useState<AllDesignsLayoutFocus | null>(null);
    const displayLayouts = [...layouts].sort((a, b) => a.roofIndex - b.roofIndex);
    const liveModuleCount = countIncludedModules(layouts, roofSelections).count;
    const wattage = form?.moduleTypeWattage;
    const liveSystemKw =
        wattage != null && wattage > 0 && liveModuleCount > 0
            ? Math.round(((liveModuleCount * wattage) / 1000) * 1000) / 1000
            : 0;
    const liveProductionKwh = estimateSelectedAnnualKwh({
        layouts,
        roofSelections,
        moduleWattage: form?.moduleTypeWattage,
    }).kwh;
    const liveMultiplier = averageIncludedMultiplier(layouts, roofSelections);

    const focusedSaved =
        layoutFocus?.kind === 'saved'
            ? designs.find((design) => designKey(design) === layoutFocus.key) ?? null
            : null;
    const focusedMax =
        layoutFocus?.kind === 'max'
            ? maxLayoutCards.find((card) => card.vendor === layoutFocus.vendor) ?? null
            : null;
    const layoutBusy =
        layoutFocus?.kind === 'saved'
            ? selectingKey === layoutFocus.key
            : Boolean(layoutFocus?.kind === 'max' && maxLayoutLoading);

    const openSavedLayout = (design: DesignData) => {
        onSelect(design);
        setLayoutFocus({ kind: 'saved', key: designKey(design) });
    };
    const openMaxLayout = (vendor: ActiveLossesVendor) => {
        onSelectMaxLayout?.(vendor);
        setLayoutFocus({ kind: 'max', vendor });
    };

    if (layoutFocus) {
        const isMax = layoutFocus.kind === 'max';
        const savedMetrics = focusedSaved ? metricsFromSavedDesign(focusedSaved) : null;
        return (
            <div
                className="pci-ai-designs pci-ai-designs--solar-panel pci-all-designs pci-all-designs--layout"
                aria-label="Selected design layout"
            >
                <header className="pci-all-designs__layout-toolbar">
                    <button
                        type="button"
                        className="pci-all-designs__layout-back"
                        onClick={() => setLayoutFocus(null)}
                    >
                        <CaretLeft size={16} weight="bold" aria-hidden />
                        Back to designs
                    </button>
                </header>
                <section
                    className={`pci-all-designs__layout-header${
                        isMax ? ' pci-all-designs__layout-header--max' : ''
                    }`}
                    aria-label="Selected design"
                >
                    <span className="pci-all-designs__eyebrow">
                        {isMax ? 'Max layout' : focusedSaved?.isAiGeneratedDesign ? 'AI design' : 'oneDRAW design'}
                    </span>
                    <strong className="pci-all-designs__title">
                        {isMax ? (
                            <>
                                <Sun size={14} weight="fill" aria-hidden />
                                {focusedMax ? lossesSourceLabel(focusedMax.vendor) : 'Max layout'}
                            </>
                        ) : (
                            <>
                                <House size={14} weight="fill" aria-hidden />
                                {focusedSaved
                                    ? savedDesignDisplayName(focusedSaved)
                                    : 'Selected design'}
                            </>
                        )}
                    </strong>
                    {layoutBusy ? (
                        <span className="pci-all-designs__status">Applying on viewport…</span>
                    ) : (
                        <span className="pci-all-designs__status">On viewport</span>
                    )}
                    <dl className="pci-all-designs__stats">
                        <div>
                            <dt>Size</dt>
                            <dd>
                                {formatKw(
                                    liveSystemKw > 0
                                        ? liveSystemKw
                                        : isMax
                                          ? maxLayoutSystemKw
                                          : focusedSaved?.System_Size_kWdc,
                                )}
                            </dd>
                        </div>
                        <div>
                            <dt>Modules</dt>
                            <dd>
                                {liveModuleCount > 0
                                    ? String(liveModuleCount)
                                    : isMax && maxLayoutModuleCount > 0
                                      ? String(maxLayoutModuleCount)
                                      : '—'}
                            </dd>
                        </div>
                        <div>
                            <dt>Production</dt>
                            <dd>
                                {formatKwh(
                                    liveProductionKwh ??
                                        (isMax
                                            ? focusedMax?.productionKwh
                                            : savedMetrics?.annualProductionKwh),
                                )}
                            </dd>
                        </div>
                        {liveMultiplier != null && liveMultiplier > 0 ? (
                            <div>
                                <dt>Multiplier</dt>
                                <dd>{Number(liveMultiplier.toFixed(3))}</dd>
                            </div>
                        ) : null}
                    </dl>
                </section>
                <div className="pci-module-panel__scroll pci-all-designs__layout-boards">
                    {displayLayouts.length > 0 ? (
                        <div className="pci-module-layout-grid">
                            {displayLayouts.map((layout) => {
                                const roofActive = layout.modules.some((cell) =>
                                    Boolean(cell?.included),
                                );
                                return (
                                    <RoofModuleBoard
                                        key={`all-designs-layout-${layout.roofIndex}`}
                                        layout={layout}
                                        multiplierDetail={
                                            roofActive
                                                ? roofIncludedMultiplierDetail(layout)
                                                : {
                                                      multiplier: null,
                                                      meanSolarAccess: null,
                                                      includedCount: 0,
                                                  }
                                        }
                                        inactive={!roofActive}
                                        includeGreyPanels={includeGreyPanels}
                                        includePurplePanels={includePurplePanels}
                                        compact
                                        readOnly={!onToggleModule || !onToggleRoof || layoutBusy}
                                        onToggleRoof={() =>
                                            onToggleRoof?.(layout.roofIndex, !roofActive)
                                        }
                                        onToggle={(moduleIndex) =>
                                            onToggleModule?.(layout.roofIndex, moduleIndex)
                                        }
                                        onHover={() => undefined}
                                    />
                                );
                            })}
                        </div>
                    ) : (
                        <p className="pci-all-designs__layout-empty">
                            {layoutBusy
                                ? 'Loading roof layout from the viewport…'
                                : 'No roof layout yet'}
                        </p>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div
            className="pci-ai-designs pci-ai-designs--solar-panel pci-all-designs"
            aria-label="All designs"
        >
            {loading && designs.length === 0 && maxLayoutCards.length === 0 ? (
                <div className="pci-ai-designs__loading" role="status">
                    <CircleNotch className="pci-ai-designs__spinner" size={18} weight="bold" />
                    <strong>Loading designs…</strong>
                </div>
            ) : (
                <ul className="pci-all-designs__grid">
                    {maxLayoutCards.map((card) => {
                        const selected = selectedMaxVendor === card.vendor;
                        const selecting = maxLayoutLoading && selected;
                        return (
                            <li key={`max-${card.vendor}`}>
                                <AllDesignsCardShell
                                    className={`pci-all-designs__card pci-all-designs__card--max pci-all-designs__card--max-${
                                        isEnFinLossesVendor(card.vendor) ? 'enfin' : 'onedraw'
                                    }${
                                        selected ? ' pci-all-designs__card--on' : ''
                                    }${selecting ? ' pci-all-designs__card--loading' : ''}`}
                                    selected={selected}
                                    selecting={selecting}
                                    onSelect={() => onSelectMaxLayout?.(card.vendor)}
                                    onViewLayout={() => openMaxLayout(card.vendor)}
                                >
                                    <span className="pci-all-designs__card-head">
                                        <span className="pci-all-designs__eyebrow">Max layout</span>
                                        {selected ? (
                                            <Check
                                                className="pci-all-designs__check"
                                                size={16}
                                                weight="bold"
                                                aria-hidden
                                            />
                                        ) : null}
                                    </span>
                                    <span className="pci-all-designs__title">
                                        <Sun size={14} weight="fill" aria-hidden />
                                        {lossesSourceLabel(card.vendor)}
                                    </span>
                                    <dl className="pci-all-designs__stats">
                                        <div>
                                            <dt>Size</dt>
                                            <dd>{formatKw(maxLayoutSystemKw)}</dd>
                                        </div>
                                        <div>
                                            <dt>Modules</dt>
                                            <dd>
                                                {maxLayoutModuleCount > 0
                                                    ? String(maxLayoutModuleCount)
                                                    : '—'}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt>Production</dt>
                                            <dd>{formatKwh(card.productionKwh)}</dd>
                                        </div>
                                        <div>
                                            <dt>PPW</dt>
                                            <dd>
                                                {card.quote?.ppw != null
                                                    ? `$${card.quote.ppw.toFixed(2)}/W`
                                                    : '—'}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt>EPC</dt>
                                            <dd>
                                                {card.quote?.epc != null
                                                    ? card.quote.epc.toLocaleString(undefined, {
                                                          style: 'currency',
                                                          currency: 'USD',
                                                          maximumFractionDigits: 0,
                                                      })
                                                    : '—'}
                                            </dd>
                                        </div>
                                    </dl>
                                    {selecting ? (
                                        <span className="pci-all-designs__status">
                                            Applying on viewport…
                                        </span>
                                    ) : selected ? (
                                        <span className="pci-all-designs__status">On viewport</span>
                                    ) : (
                                        <span className="pci-all-designs__hint">
                                            Preview this losses stack
                                        </span>
                                    )}
                                </AllDesignsCardShell>
                            </li>
                        );
                    })}
                    {designs.map((design) => {
                        const key = designKey(design);
                        const selected = selectedKey === key && selectedMaxVendor == null;
                        const selecting = selectingKey === key;
                        const savedMetrics = metricsFromSavedDesign(design);
                        const isAi =
                            Boolean(design.isAiGeneratedDesign) ||
                            Boolean(
                                (design as { IsAiGeneratedDesign?: boolean }).IsAiGeneratedDesign,
                            );
                        return (
                            <li key={key}>
                                <AllDesignsCardShell
                                    className={`pci-all-designs__card${
                                        selected ? ' pci-all-designs__card--on' : ''
                                    }${selecting ? ' pci-all-designs__card--loading' : ''}`}
                                    selected={selected}
                                    selecting={selecting}
                                    onSelect={() => onSelect(design)}
                                    onViewLayout={() => openSavedLayout(design)}
                                >
                                    <span className="pci-all-designs__card-head">
                                        {isAi ? (
                                            <span className="pci-all-designs__eyebrow pci-all-designs__eyebrow--ai">
                                                <Sparkle size={11} weight="fill" aria-hidden />
                                                AI design
                                            </span>
                                        ) : (
                                            <span className="pci-all-designs__eyebrow">
                                                oneDRAW design
                                            </span>
                                        )}
                                        {selected ? (
                                            <Check
                                                className="pci-all-designs__check"
                                                size={16}
                                                weight="bold"
                                                aria-hidden
                                            />
                                        ) : null}
                                    </span>
                                    <span className="pci-all-designs__title">
                                        <House
                                            size={14}
                                            weight={selected ? 'fill' : 'regular'}
                                            aria-hidden
                                        />
                                        {savedDesignDisplayName(design)}
                                    </span>
                                    <span className="pci-all-designs__meta">
                                        {design.User_Created_Id}
                                        {design.User_Department
                                            ? ` · ${design.User_Department}`
                                            : ''}
                                    </span>
                                    <span className="pci-all-designs__meta">
                                        {getLocalTime(design.User_Created_Timestamp)}
                                    </span>
                                    <dl className="pci-all-designs__stats">
                                        <div>
                                            <dt>Size</dt>
                                            <dd>{formatKw(design.System_Size_kWdc)}</dd>
                                        </div>
                                        {savedMetrics.annualProductionKwh != null ? (
                                            <div>
                                                <dt>Production</dt>
                                                <dd>
                                                    {formatKwh(savedMetrics.annualProductionKwh)}
                                                </dd>
                                            </div>
                                        ) : null}
                                        {showLosses ? (
                                            <div>
                                                <dt>Losses</dt>
                                                <dd>
                                                    {isEnFinLossesVendor(design.MultipliersVendor)
                                                        ? 'EnFin'
                                                        : 'oneDRAW'}
                                                </dd>
                                            </div>
                                        ) : null}
                                    </dl>
                                    {selecting ? (
                                        <span className="pci-all-designs__status">
                                            Opening on viewport…
                                        </span>
                                    ) : selected ? (
                                        <span className="pci-all-designs__status">On viewport</span>
                                    ) : null}
                                </AllDesignsCardShell>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export function savedDesignKey(design: Pick<DesignData, 'Design_No' | 'Version_No'>): string {
    return `${design.Design_No}-${design.Version_No}`;
}

export function savedDesignDisplayName(
    design: Pick<DesignData, 'Design_No' | 'Version_No'>,
): string {
    return `Design : ${design.Design_No} - Version : ${design.Version_No}`;
}

export function metricsFromSavedDesign(design: DesignData): PreContractPpwMetricsOverride {
    const snap = design as DesignData & Record<string, unknown>;
    const productionObject =
        snap.Production && typeof snap.Production === 'object'
            ? (snap.Production as Record<string, unknown>)
            : null;
    const production =
        annualProductionKwhFromSnapshot(snap) ??
        annualProductionKwhForPricingFromRaw(
            parseProductionRawScalar(productionObject?.ActualAnnual),
        ) ??
        annualProductionKwhForPricingFromRaw(parseProductionRawScalar(snap.ProductionDouble));
    const moduleCount =
        typeof design.ModulesCount === 'number' && design.ModulesCount > 0
            ? design.ModulesCount
            : null;
    return {
        systemKw:
            typeof design.System_Size_kWdc === 'number' && design.System_Size_kWdc > 0
                ? design.System_Size_kWdc
                : null,
        annualProductionKwh: production,
        moduleCount,
        multiplier: null,
    };
}

export default PreContractSavedDesignsPanel;
